import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useMutation } from 'react-apollo';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import DadosPagamentoAlternativoForm from '../dados-pagamento-alternativo/DadosPagamentoAlternativoForm';
import RenderTextField from '../../../../components/TextField/RenderTextField';
import { SAVE_DADOS_PAGAMENTO_ALTERNATIVO_CONVENIO } from '../../../../graphql/mutations';

const ModalConvenioAlternativo = ({ convenio, unidade, onClose, onSuccess, frontWebUrl }) => {
  const isEdicao = !!convenio;
  const dadosPagamentosIniciais = convenio?.dadosPagamentoAlternativo || {};

  const [novaDescricao, setNovaDescricao] = useState('');
  const [dadosPagamento, setDadosPagamento] = useState({
    id: dadosPagamentosIniciais.id || null,
    slug: dadosPagamentosIniciais.slug || '',
    walletId: dadosPagamentosIniciais.walletId || '',
    percentualRepasse: dadosPagamentosIniciais.percentualRepasse ?? '',
  });
  const [notification, setNotification] = useState({ isOpen: false, variant: '', message: '' });

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  const [saveDadosPagamento] = useMutation(SAVE_DADOS_PAGAMENTO_ALTERNATIVO_CONVENIO);

  const slug = convenio?.slugAsaas || '';
  const linkAgendamento = (() => {
    if (!frontWebUrl || !slug) return null;
    const baseUrl = frontWebUrl.endsWith('/') ? frontWebUrl.slice(0, -1) : frontWebUrl;
    return `${baseUrl}/pesquisa/consulta?parceiro=${slug}`;
  })();

  const handleCopiarLink = async () => {
    if (!linkAgendamento) return;
    try {
      await navigator.clipboard.writeText(linkAgendamento);
      setNotification({ isOpen: true, variant: 'success', message: 'Link copiado para a área de transferência' });
    } catch {
      setNotification({ isOpen: true, variant: 'error', message: 'Erro ao copiar link' });
    }
  };

  const convenioPayload = isEdicao
    ? { id: convenio.id, descricao: convenio.descricao, unidade: unidade ? { id: unidade.id } : undefined }
    : { descricao: novaDescricao, unidade: unidade ? { id: unidade.id } : undefined };

  const handleSalvar = async () => {
    if (!dadosPagamento.slug || (!isEdicao && !novaDescricao)) return;
    try {
      setSalvando(true);
      setErro(null);
      await saveDadosPagamento({
        variables: {
          convenio: convenioPayload,
          dados: {
            ...(dadosPagamento.id ? { id: dadosPagamento.id } : {}),
            slug: dadosPagamento.slug,
            walletId: dadosPagamento.walletId || null,
            percentualRepasse: dadosPagamento.percentualRepasse !== '' ? Number(dadosPagamento.percentualRepasse) : null,
          },
        },
      });
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      setErro(e?.graphQLErrors?.[0]?.message || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Container>
      {isEdicao ? (
        <Title>Dados de Pagamento — {convenio?.descricao}</Title>
      ) : (
        <RenderTextField
          label="Descrição do Convênio *"
          field={novaDescricao}
          handleChange={(e) => setNovaDescricao(e.target.value)}
        />
      )}

      {isEdicao && linkAgendamento && (
        <LinkContainer>
          <LinkLabel>Link de Agendamento</LinkLabel>
          <LinkRow>
            <LinkInput value={linkAgendamento} readOnly />
            <Button
              variant="outlined"
              size="small"
              onClick={handleCopiarLink}
              style={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              Copiar
            </Button>
          </LinkRow>
        </LinkContainer>
      )}

      <DadosPagamentoAlternativoForm dados={dadosPagamento} onChange={setDadosPagamento} />

      {erro && <Erro>{erro}</Erro>}

      <Actions>
        <Button onClick={onClose} disabled={salvando}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSalvar}
          disabled={salvando || !dadosPagamento.slug || (!isEdicao && !novaDescricao)}
        >
          {salvando ? <CircularProgress size={18} /> : 'Salvar'}
        </Button>
      </Actions>

      {notification.isOpen && (
        <NotificationInline variant={notification.variant}>
          {notification.message}
        </NotificationInline>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const Erro = styled.span`
  color: red;
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const LinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LinkLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #555;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  background: #f9f9f9;
  color: #333;
  cursor: text;
`;

const NotificationInline = styled.div`
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  background: ${({ variant }) => variant === 'success' ? '#e8f5e9' : '#ffebee'};
  color: ${({ variant }) => variant === 'success' ? '#2e7d32' : '#c62828'};
`;

export default ModalConvenioAlternativo;
